import { z } from 'zod';

// What the scoped `sbc-customer-*` tools hand the model back. These are wire contracts for
// data the platform does not own — the storefront's own tables and its schedule endpoint —
// so every field is parsed through here before it reaches a prompt, and a drift on the SBC
// side surfaces as a tool error instead of a silently wrong fact quoted to a member.
//
// Each result carries `identified`. The gateway resolves the customer at ingress and pins
// them onto the run; when it could not, the tools return `identified: false` rather than
// guessing, so the model is told plainly that it has no account to read.

export const UNIDENTIFIED_CUSTOMER_REASON =
  'No customer is linked to this conversation, so no account facts are available. Do not ' +
  'guess or state any account fact — answer from the thread alone, or say a teammate will ' +
  'check the account.';

export const UnidentifiedSchema = z.object({
  identified: z.literal(false),
  reason: z.string(),
});

export const CustomerProfileSchema = z.object({
  userId: z.uuid(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  shippingName: z.string().nullable(),
  signedUpAt: z.string().nullable(),
});

export const CustomerProfileResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    profile: CustomerProfileSchema,
  }),
  UnidentifiedSchema,
]);
export type CustomerProfileResult = z.infer<typeof CustomerProfileResultSchema>;

export const CustomerSubscriptionSchema = z.object({
  status: z.string(),
  billingPlan: z.string().nullable(),
  planType: z.string().nullable(),
  deliveryFrequency: z.string().nullable(),
  nextChargeAt: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  canceledAt: z.string().nullable(),
});

export const CustomerStarterTrialSchema = z.object({
  status: z.string(),
  trialStart: z.string().nullable(),
  canceledAt: z.string().nullable(),
  // The trial converts itself into a paid subscription ~14 days after delivery unless the
  // member actively cancels, so "no active subscription" is the wrong answer to a trial
  // member asking to cancel. Derived here rather than left to the model to infer.
  willAutoConvert: z.boolean(),
});

export const UnavailableSectionSchema = z.object({ available: z.literal(false) });

export const CustomerAddOnCreditSchema = z.object({
  remainingCents: z.number(),
  expiresAt: z.string().nullable(),
  source: z.string(),
  grantedAt: z.string().nullable(),
});

export const CustomerSubscriptionResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    // Newest first. A member can hold several rows over time; the live one is whichever is
    // active, which the model reads from `status` rather than being told by position.
    subscriptions: z.array(CustomerSubscriptionSchema),
    starterTrial: CustomerStarterTrialSchema.nullable(),
    addOnCredits: z.union([z.array(CustomerAddOnCreditSchema), UnavailableSectionSchema]),
  }),
  UnidentifiedSchema,
]);
export type CustomerSubscriptionResult = z.infer<typeof CustomerSubscriptionResultSchema>;

export const FDA_HOLD_STATUSES = ['on_hold', 'partially_released', 'released'] as const;
export const FdaHoldStatusSchema = z.enum(FDA_HOLD_STATUSES);
export type FdaHoldStatus = z.infer<typeof FdaHoldStatusSchema>;

export const OrderFdaHoldSchema = z.object({
  batch: z.string(),
  status: FdaHoldStatusSchema,
});
export type OrderFdaHold = z.infer<typeof OrderFdaHoldSchema>;

export const FdaHoldSheetStateSchema = z.object({
  lastSyncedAt: z.string().nullable(),
  stale: z.boolean(),
});
export type FdaHoldSheetState = z.infer<typeof FdaHoldSheetStateSchema>;

export const ACI_STAGE_LABELS: Readonly<Record<string, string>> = {
  '100': 'Order received',
  '200': 'Received at warehouse',
  '300': 'Shipped from warehouse',
  '350': 'In transit',
  '400': 'Arrived in destination country',
  '450': 'In customs clearance',
  '480': 'Customs cleared',
  '490': 'In local delivery',
  '500': 'Out for delivery',
  '600': 'Delivered',
  '999': 'Delivery failed',
  '-200': 'Invalid tracking',
};

export const StorefrontOrderSchema = z.object({
  id: z.uuid(),
  number: z.string().nullable(),
  kind: z.string().nullable(),
  status: z.string().nullable(),
  paymentStatus: z.string().nullable().default(null),
  parcelStatus: z.string().nullable().default(null),
  warehouse: z.string().nullable().default(null),
  providerStatus: z.string().nullable(),
  providerTrackingStatus: z.string().nullable(),
  trackingUrl: z.string().nullable(),
  lastMileCarrier: z.string().nullable(),
  lastMileTrackingNumber: z.string().nullable(),
  shipToCountry: z.string().nullable().default(null),
  shipToState: z.string().nullable().default(null),
  shipToPostalCode: z.string().nullable().default(null),
  placedAt: z.string().nullable(),
  dispatchedAt: z.string().nullable().default(null),
  deliveredAt: z.string().nullable(),
});

export const OrderItemSchema = z.object({
  name: z.string(),
  quantity: z.number().nullable(),
  addOn: z.boolean(),
  gift: z.boolean(),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

export const ParcelStageSchema = z.object({
  stage: z.string(),
  at: z.string().nullable(),
  current: z.boolean(),
});

export const CarrierScanSchema = z.object({
  at: z.string(),
  place: z.string().nullable(),
  event: z.string(),
});

export const ParcelTrackingSchema = z.object({
  stages: z.array(ParcelStageSchema),
  carrierScans: z.array(CarrierScanSchema),
  lastMovedAt: z.string().nullable(),
});
export type ParcelTracking = z.infer<typeof ParcelTrackingSchema>;

export const CarrierTrackingSchema = z.object({
  carrier: z.string(),
  status: z.string().nullable(),
  delivered: z.boolean(),
  estimatedDelivery: z.string().nullable(),
  scans: z.array(CarrierScanSchema),
  lastMovedAt: z.string().nullable(),
  deliveryPhotos: z.number().nullable(),
});
export type CarrierTracking = z.infer<typeof CarrierTrackingSchema>;

export const OrderWarehouseIssueSchema = z.object({
  code: z.string(),
  message: z.string().nullable(),
  at: z.string().nullable(),
});
export type OrderWarehouseIssue = z.infer<typeof OrderWarehouseIssueSchema>;

export const CustomerOrderSchema = StorefrontOrderSchema.extend({
  warehouseStage: z.string().nullable(),
  warehouseIssue: OrderWarehouseIssueSchema.nullable().default(null),
  items: z.array(OrderItemSchema),
  moreItems: z.number().int().nonnegative().default(0),
  fdaHold: OrderFdaHoldSchema.nullable(),
  tracking: z.union([ParcelTrackingSchema, UnavailableSectionSchema]).nullable().default(null),
  carrierTracking: z
    .union([CarrierTrackingSchema, UnavailableSectionSchema])
    .nullable()
    .default(null),
  proofOfDelivery: z.string().nullable().default(null),
});

export const CustomerOrdersResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    orders: z.array(CustomerOrderSchema),
    fdaHoldSheet: FdaHoldSheetStateSchema,
  }),
  UnidentifiedSchema,
]);
export type CustomerOrdersResult = z.infer<typeof CustomerOrdersResultSchema>;

export const CustomerRefundSchema = z.object({
  amount: z.number().nullable(),
  currency: z.string().nullable(),
  status: z.string().nullable(),
  createdAt: z.string().nullable(),
  reference: z.string().nullable(),
  referenceStatus: z.string().nullable(),
});
export type CustomerRefund = z.infer<typeof CustomerRefundSchema>;

export const ChargeDisputeSchema = z.object({
  status: z.string().nullable(),
  reason: z.string().nullable(),
  amount: z.number().nullable(),
  currency: z.string().nullable(),
  createdAt: z.string().nullable(),
  evidenceDueBy: z.string().nullable(),
});
export type ChargeDispute = z.infer<typeof ChargeDisputeSchema>;

// What Stripe recorded against this member's billing account, newest first. Amounts stay
// in the currency's minor unit exactly as Stripe holds them — converting here would invent
// a rounding rule, and the model quotes the invoice, not our arithmetic.
export const CustomerChargeSchema = z.object({
  amount: z.number().nullable(),
  amountRefunded: z.number().nullable(),
  currency: z.string().nullable(),
  status: z.string().nullable(),
  refunded: z.boolean().nullable(),
  disputed: z.boolean().nullable().default(null),
  description: z.string().nullable(),
  failureMessage: z.string().nullable(),
  createdAt: z.string().nullable(),
  refunds: z.union([z.array(CustomerRefundSchema), UnavailableSectionSchema]).default([]),
  dispute: z.union([ChargeDisputeSchema, UnavailableSectionSchema]).nullable().default(null),
});

export const BILLING_ITEM_KINDS = ['plan', 'shipping', 'customs_duty', 'other'] as const;

export const BillingSubscriptionItemSchema = z.object({
  kind: z.enum(BILLING_ITEM_KINDS),
  label: z.string(),
  unitAmount: z.number().nullable(),
  quantity: z.number().nullable(),
  currency: z.string().nullable(),
  interval: z.string().nullable(),
  intervalCount: z.number().nullable(),
});

export const BillingDiscountSchema = z.object({
  name: z.string().nullable(),
  percentOff: z.number().nullable(),
  amountOff: z.number().nullable(),
  duration: z.string().nullable(),
});

export const BillingSubscriptionSchema = z.object({
  status: z.string().nullable(),
  nextChargeAt: z.string().nullable(),
  cancelAt: z.string().nullable(),
  canceledAt: z.string().nullable(),
  items: z.array(BillingSubscriptionItemSchema),
  discounts: z.array(BillingDiscountSchema),
});
export type BillingSubscription = z.infer<typeof BillingSubscriptionSchema>;
export type BillingDiscount = z.infer<typeof BillingDiscountSchema>;

export const InvoiceDiscountSchema = z.object({
  name: z.string().nullable(),
  amount: z.number().nullable(),
});

export const InvoiceShippingSchema = z.object({
  description: z.string().nullable(),
  amount: z.number(),
});

export const BillingInvoiceSchema = z.object({
  number: z.string().nullable(),
  createdAt: z.string().nullable(),
  status: z.string().nullable(),
  billingReason: z.string().nullable(),
  subtotal: z.number().nullable().default(null),
  discounts: z.array(InvoiceDiscountSchema).default([]),
  tax: z.number().nullable().default(null),
  shipping: InvoiceShippingSchema.nullable().default(null),
  total: z.number().nullable(),
  storeCreditApplied: z.number().nullable().default(null),
  amountPaid: z.number().nullable(),
  currency: z.string().nullable(),
  lines: z.array(
    z.object({
      description: z.string().nullable(),
      amount: z.number().nullable(),
      quantity: z.number().nullable(),
    }),
  ),
});
export type BillingInvoice = z.infer<typeof BillingInvoiceSchema>;

export const BillingAccountSchema = z.object({
  creditBalance: z.number().nullable(),
  currency: z.string().nullable(),
  defaultCard: z
    .object({
      brand: z.string().nullable(),
      last4: z.string().nullable(),
      expMonth: z.number().nullable(),
      expYear: z.number().nullable(),
    })
    .nullable(),
});
export type BillingAccount = z.infer<typeof BillingAccountSchema>;

export const CustomerChargesResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    // Empty when the member has no Stripe customer record at all, which is a different
    // answer from "no charges" and is why `billingAccount` is reported separately.
    billingAccount: z.boolean(),
    charges: z.array(CustomerChargeSchema),
    subscriptions: z
      .union([z.array(BillingSubscriptionSchema), UnavailableSectionSchema])
      .default([]),
    invoices: z.union([z.array(BillingInvoiceSchema), UnavailableSectionSchema]).default([]),
    account: z.union([BillingAccountSchema, UnavailableSectionSchema]).nullable().default(null),
  }),
  UnidentifiedSchema,
]);
export type CustomerChargesResult = z.infer<typeof CustomerChargesResultSchema>;

// The storefront owns the cadence maths (which cycle is an on-month, when the next box
// lands, when curation closes) and serves it over one endpoint. Mirrors the response of
// GET /api/agent-platform/customer-schedule in the seoulbeautyclub repo — snake_case
// because that is the wire, not our naming.
export const MemberScheduleSchema = z.object({
  plan: z.string(),
  prepaid: z.boolean(),
  boxes_per_cycle: z.number(),
  delivery_every_n_months: z.number(),
  box_due_this_month: z.boolean().nullable(),
  next_box_date: z.string().nullable(),
  boxes_remaining: z.number(),
  next_renewal: z.string().nullable(),
  must_curate_now: z.boolean(),
});

export const CustomerScheduleSchema = z.object({
  user_id: z.string(),
  found: z.boolean(),
  box_due_this_cycle: z.boolean().nullable(),
  next_box_date: z.string().nullable(),
  last_curation_day: z.string().nullable(),
  schedule: MemberScheduleSchema.nullable(),
});
export type CustomerSchedule = z.infer<typeof CustomerScheduleSchema>;

export const CustomerScheduleResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    schedule: CustomerScheduleSchema,
  }),
  UnidentifiedSchema,
]);
export type CustomerScheduleResult = z.infer<typeof CustomerScheduleResultSchema>;

export const LOOKUP_IDENTIFIER_NOT_IN_CONVERSATION = 'identifier not in the conversation';

export const CUSTOMER_LOOKUP_NOTE =
  'This account was found from an email, order number, tracking number, phone number, name or ' +
  'address given in the conversation, not from the account linked to this thread. CS must ' +
  'confirm the member owns it before sharing any of its details.';

export const SEVERAL_MEMBERS_MATCH = 'several members match';

const LookupUnavailableSchema = z.object({
  available: z.literal(false),
  reason: z.string(),
});

export const TRACKING_NUMBER_NOT_IN_CONVERSATION = 'tracking number not in the conversation';
export const CARRIER_NOT_SUPPORTED = 'carrier not supported';

export const CarrierTrackingResultSchema = z.union([
  CarrierTrackingSchema.extend({
    available: z.literal(true),
    trackingNumber: z.string(),
    checkedAt: z.string(),
  }),
  z.object({
    available: z.literal(false),
    reason: z.literal(CARRIER_NOT_SUPPORTED),
    carrier: z.string().nullable(),
  }),
  LookupUnavailableSchema,
]);
export type CarrierTrackingResult = z.infer<typeof CarrierTrackingResultSchema>;

export const CustomerLookupResultSchema = z.union([
  z.object({
    available: z.literal(true),
    lookedUpBy: z.enum(['email', 'order', 'tracking', 'phone', 'name', 'address']),
    note: z.string(),
    linkedAccount: z.boolean(),
    profile: z.union([CustomerProfileResultSchema, UnavailableSectionSchema]),
    subscription: z.union([CustomerSubscriptionResultSchema, UnavailableSectionSchema]),
    orders: z.union([CustomerOrdersResultSchema, UnavailableSectionSchema]),
    charges: z.union([CustomerChargesResultSchema, UnavailableSectionSchema]),
  }),
  LookupUnavailableSchema,
]);
export type CustomerLookupResult = z.infer<typeof CustomerLookupResultSchema>;

export function isEmptyLookedUpAccount(result: CustomerLookupResult): boolean {
  if (!result.available) return false;
  const { orders, charges, subscription } = result;
  return (
    'orders' in orders &&
    orders.orders.length === 0 &&
    'charges' in charges &&
    charges.charges.length === 0 &&
    'subscriptions' in subscription &&
    subscription.subscriptions.length === 0 &&
    subscription.starterTrial === null
  );
}

export const GiftCardRowSchema = z.object({
  code: z.string(),
  amountCents: z.number(),
  status: z.string(),
  purchaserFirstName: z.string().nullable(),
  recipientFirstName: z.string().nullable(),
  purchasedAt: z.string().nullable(),
  redeemedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  redeemedAs: z.string().nullable(),
  payoutComplete: z.boolean().nullable(),
  purchasedByMember: z.boolean(),
  receivedByMember: z.boolean(),
});
export type GiftCardRow = z.infer<typeof GiftCardRowSchema>;

export const GiftCardSchema = GiftCardRowSchema.omit({ code: true }).extend({
  code: z.string().nullable(),
  codeLast4: z.string(),
  redeemed: z.boolean(),
  redeemUrl: z.string().nullable(),
});
export type GiftCard = z.infer<typeof GiftCardSchema>;

export const GiftCardResultSchema = z.union([
  z.object({
    available: z.literal(true),
    lookedUpBy: z.enum(['code', 'member']),
    giftCards: z.array(GiftCardSchema),
  }),
  LookupUnavailableSchema,
  UnidentifiedSchema,
]);
export type GiftCardResult = z.infer<typeof GiftCardResultSchema>;

export const ResubscribeCodeResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    issued: z.literal(true),
    reused: z.boolean(),
    code: z.string(),
    percentOff: z.number().nullable(),
    expiresAt: z.string().nullable(),
  }),
  z.object({
    identified: z.literal(true),
    issued: z.literal(false),
    reason: z.string(),
  }),
  UnidentifiedSchema,
]);
export type ResubscribeCodeResult = z.infer<typeof ResubscribeCodeResultSchema>;
